import { uniqBy } from 'lodash-es'
import moment from 'moment'
import type { InboxConversation } from '@/utils/functions'

/**
 * All and Unread filter client-side over one shared, already-fetched result set, so they share the
 * `all` server key. Only the AI tab issues its own server request (`ai_texts=true`, sorted by
 * `-last_ai_message_at`), so it gets its own bucket. An unrecognized tab value falls back to `all`
 * rather than throwing, since callers pass through whatever `activeTab` currently is.
 */
export type InboxServerKey = 'all' | 'ai'

export const serverKeyForTab = (tab: string): InboxServerKey =>
  tab === 'ai' ? 'ai' : 'all'

/** Per-tab list state. Cursor-paginated, so `complete` marks end-of-results
 * separately from `loaded` (has this bucket ever been fetched at all). */
export interface InboxTabBucket {
  rows: InboxConversation[]
  cursor: string
  complete: boolean
  loaded: boolean
}

/**
 * Always returns a fresh object with its own `rows` array. Two buckets must
 * never share a `rows` reference — that would silently alias them, so
 * mutating one (e.g. an unshift from a new chat message) would corrupt the
 * other.
 */
export const emptyBucket = (): InboxTabBucket => ({
  rows: [],
  cursor: '',
  complete: false,
  loaded: false
})

/**
 * Fresh bucket state for every server key. Used both as the initial state and
 * as the return value of `invalidateInboxBuckets` — a filter change (location,
 * account manager, search) applies to both server keys, so both must be
 * thrown away and re-fetched. A tab switch alone must not call this.
 */
export const emptyInboxBuckets = (): Record<
  InboxServerKey,
  InboxTabBucket
> => ({
  all: emptyBucket(),
  ai: emptyBucket()
})

/**
 * Resets every bucket. Same shape as `emptyInboxBuckets`, named separately so
 * call sites read as "a filter changed, throw everything away" rather than
 * "build the initial state" — the two happen for different reasons even
 * though they compute the same value.
 */
export const invalidateInboxBuckets = (): Record<
  InboxServerKey,
  InboxTabBucket
> => emptyInboxBuckets()

/**
 * Matches the server's `-last_ai_message_at` ordering, so the optimistic subset painted from the `all`
 * bucket lines up with the real AI-tab response once it arrives. Rows with no timestamp (or an
 * unparsable one) sort last instead of throwing or corrupting the sort — `last_ai_message_at` is only
 * ever populated for customers with at least one AI message, so a missing value just means "not
 * applicable," not "invalid data."
 */
export const byAiRecencyDesc = (
  a: InboxConversation,
  b: InboxConversation
): number => {
  const aTime = a.last_ai_message_at ? Date.parse(a.last_ai_message_at) : NaN
  const bTime = b.last_ai_message_at ? Date.parse(b.last_ai_message_at) : NaN
  const aValid = Number.isFinite(aTime)
  const bValid = Number.isFinite(bTime)

  if (!aValid && !bValid) return 0
  if (!aValid) return 1
  if (!bValid) return -1

  return bTime - aTime
}

/**
 * The optimistic AI-tab paint: rows already sitting in the `all` bucket that qualify for the AI tab,
 * newest-first. `limit` (pass the page size) caps the count at what the first real page can return —
 * without it, painting more optimistic rows than a page holds makes the list visibly shrink when the
 * response lands and replaces them, which is worse than the flicker this is meant to avoid.
 */
export const optimisticAiRows = (
  allRows: InboxConversation[],
  limit?: number
): InboxConversation[] => {
  const rows = allRows
    .filter(row => Boolean(row.last_ai_message_at))
    .sort(byAiRecencyDesc)

  return typeof limit === 'number' ? rows.slice(0, limit) : rows
}

/**
 * Pulls the `cursor` query param off a DRF-style `next` pagination URL. Returns `null` for a
 * missing/empty URL, or one that fails to parse (a relative or malformed value), rather than
 * throwing — the caller treats a `null` cursor the same as "no more pages".
 */
export const parseNextCursor = (
  nextUrl: string | null | undefined
): string | null => {
  if (!nextUrl) return null

  try {
    const url = new URL(nextUrl)

    return new URLSearchParams(url.search).get('cursor')
  } catch {
    return null
  }
}

export interface InboxPage {
  results?: InboxConversation[] | null
  next?: string | null
}

/**
 * Pure reducer: given a bucket's current state and a freshly-fetched page, returns the bucket's next
 * state. Never mutates `bucket` or `page.results` — the caller (`Inbox.vue`) applies the result with
 * `Object.assign` so Vue's reactivity fires per-key on an already-observed object.
 */
export const nextBucketState = (
  bucket: InboxTabBucket,
  page: InboxPage
): InboxTabBucket => {
  const rows = page.results?.length
    ? uniqBy([...bucket.rows, ...page.results], 'customer_id')
    : bucket.rows

  const nextCursor = parseNextCursor(page.next)
  const complete = !page.next || !nextCursor || nextCursor === bucket.cursor

  return {
    rows,
    cursor: complete ? '' : (nextCursor as string),
    complete,
    loaded: true
  }
}

export const visibleRowsFor = (
  activeTab: string,
  buckets: Record<InboxServerKey, InboxTabBucket>,
  pageSize: number
): InboxConversation[] => {
  const bucket = buckets[serverKeyForTab(activeTab)]

  if (activeTab === 'ai' && !bucket.loaded) {
    return optimisticAiRows(buckets.all.rows, pageSize)
  }

  if (activeTab === 'unread') {
    return bucket.rows.filter(m => Number(m.unread_messages) > 0)
  }

  return bucket.rows
}

export interface InboxDateGroup {
  key: 'today' | 'yesterday' | 'week' | 'older'
  label: string
  items: InboxConversation[]
}

/**
 * The field a row is sorted, grouped, and dated by: `last_ai_message_at` on the AI tab,
 * `last_message_created` elsewhere. Date header and row time both read it, so they cannot disagree.
 * Grouping the AI tab by `last_message_created` re-sorted the list by that field — a row's group
 * outranks its position within it. Missing values pass through, rendering as "-".
 */
export const rowRecencyTimestamp = (
  row: InboxConversation,
  activeTab: string
): string | null | undefined =>
  activeTab === 'ai' ? row.last_ai_message_at : row.last_message_created

/**
 * Date headers keyed on `rowRecencyTimestamp`, preserving incoming order within
 * each group. `now` is injectable for tests; boundaries are local midnight.
 * Missing/unparsable timestamps group as `older`, never `today` —
 * `moment(undefined)` is *now*. Validity uses `Date.parse`, as `byAiRecencyDesc`.
 */
export const groupRowsByDate = (
  rows: InboxConversation[],
  activeTab: string,
  now?: moment.MomentInput
): InboxDateGroup[] => {
  const groups: InboxDateGroup[] = [
    { key: 'today', label: 'Today', items: [] },
    { key: 'yesterday', label: 'Yesterday', items: [] },
    { key: 'week', label: 'This week', items: [] },
    { key: 'older', label: 'Older', items: [] }
  ]
  const [today, yesterday, week, older] = groups

  const reference = moment(now)
  const startOfToday = reference.clone().startOf('day')
  const startOfYesterday = reference.clone().subtract(1, 'day').startOf('day')
  const startOfWeek = reference.clone().subtract(7, 'days').startOf('day')

  for (const row of rows) {
    const timestamp = rowRecencyTimestamp(row, activeTab)
    const parsed = timestamp ? Date.parse(timestamp) : NaN
    const when = Number.isFinite(parsed) ? moment(parsed) : null

    if (!when) {
      older.items.push(row)
    } else if (when.isSameOrAfter(startOfToday)) {
      today.items.push(row)
    } else if (when.isSameOrAfter(startOfYesterday)) {
      yesterday.items.push(row)
    } else if (when.isSameOrAfter(startOfWeek)) {
      week.items.push(row)
    } else {
      older.items.push(row)
    }
  }

  return groups.filter(group => group.items.length > 0)
}

export interface InboxLocationFilterState {
  location: string
  selectedAccountManagers: unknown[]
}

/**
 * Returns an independent copy of the location/account-manager fields (a new array, not the same
 * reference) from `source`. `Inbox.vue` uses this both directions: to seed the filter popover's draft
 * from the applied `filters` when it opens, and to commit the draft back onto `filters` on Apply.
 */
export const copyLocationFilterState = (
  source: InboxLocationFilterState
): InboxLocationFilterState => ({
  location: source.location,
  selectedAccountManagers: [...(source.selectedAccountManagers || [])]
})

export interface InboxTabDescriptor {
  value: string
  label: string
  count: number | null
}

/**
 * Builds the tab strip's descriptors. `count: null` (not a `hasCount` flag)
 * is what suppresses the badge in the template's `v-if="tab.count != null"`,
 * and the AI tab is only appended when the merchant has the flag on — both
 * silent-failure-prone constraints worth pinning with a test.
 */
export const tabsFor = ({
  allRowsCount,
  totalUnread,
  aiTextsEnabled
}: {
  allRowsCount: number
  totalUnread: number
  aiTextsEnabled: boolean
}): InboxTabDescriptor[] => {
  const tabs: InboxTabDescriptor[] = [
    { value: 'all', label: 'All', count: allRowsCount || null },
    { value: 'unread', label: 'Unread', count: totalUnread || null }
  ]

  if (aiTextsEnabled) {
    tabs.push({ value: 'ai', label: 'AI Texts', count: null })
  }

  return tabs
}

/**
 * Single-flight cache keyed by `key`: a second call while `factory`'s promise
 * is outstanding returns that same promise instead of invoking `factory`
 * again. Clears its own slot in `map` on settle (success or rejection) — but
 * only if it still owns that slot, so a slower, superseded request can't
 * clobber a newer one that already replaced it.
 */
export const inFlight = <T>(
  map: Record<string, Promise<T> | null>,
  key: string,
  factory: () => Promise<T>
): Promise<T> => {
  const existing = map[key]
  if (existing) return existing

  const request = factory().finally(() => {
    if (map[key] === request) {
      map[key] = null
    }
  })

  map[key] = request

  return request
}
